// ---------------------------------------------------------------------------
// Prezio Onboard Widget — i18n
// ---------------------------------------------------------------------------
// Simple key-based i18n with nested key support and parameter interpolation.
// Only `en` is statically bundled. Other locales (sv, fr, pt, da, nb, fi, nl,
// de, es, pl, ro, it) are loaded on demand via dynamic import() — Vite
// code-splits them into separate chunks for ESM consumers.
//
// Widget UI chrome and fixed labels keyed by stable id (e.g. tariff types)
// live here. Property labels come from the API, which follows the chosen
// locale through the `Accept-Language` header (see `src/api/client.ts`).
// ---------------------------------------------------------------------------

import en from '@/i18n/locales/en.json';
import { DEFAULT_LOCALE } from '@/utils/constants';
import type { Locale } from '@/types/config';

type LocaleMessages = Record<string, unknown>;

/** Eagerly-bundled locales (only English). */
const bundled: Record<string, LocaleMessages> = { en };

/** Cache for dynamically-loaded or manually-registered locales. */
const loaded: Record<string, LocaleMessages> = {};

/** The currently active locale. Set via `setLocale()` or `loadLocale()`. */
let activeLocale: string = DEFAULT_LOCALE;

/** The locale a host or the resolver explicitly chose; `null` until then. */
let chosenLocale: string | null = null;

// ---------------------------------------------------------------------------
// Lazy loaders — one per locale with explicit static paths so bundlers can
// code-split them. Template-literal imports would prevent tree-shaking.
// ---------------------------------------------------------------------------

// Typed against `Locale` so adding a code without a loader fails to compile.
const LOCALE_LOADERS: Record<
  Exclude<Locale, typeof DEFAULT_LOCALE>,
  () => Promise<{ default: LocaleMessages }>
> = {
  sv: () => import('@/i18n/locales/sv.json'),
  fr: () => import('@/i18n/locales/fr.json'),
  pt: () => import('@/i18n/locales/pt.json'),
  da: () => import('@/i18n/locales/da.json'),
  nb: () => import('@/i18n/locales/nb.json'),
  fi: () => import('@/i18n/locales/fi.json'),
  nl: () => import('@/i18n/locales/nl.json'),
  de: () => import('@/i18n/locales/de.json'),
  es: () => import('@/i18n/locales/es.json'),
  pl: () => import('@/i18n/locales/pl.json'),
  ro: () => import('@/i18n/locales/ro.json'),
  it: () => import('@/i18n/locales/it.json'),
};

/** Browser `no` (Norwegian) and `nn` (Nynorsk) are served by the `nb` file. */
const BROWSER_LOCALE_ALIASES: Record<string, Locale> = { no: 'nb', nn: 'nb' };

/** Widget locales whose backend language code differs (backend columns are `*_no`). */
const API_LANGUAGE: Partial<Record<Locale, string>> = { nb: 'no' };

// ---------------------------------------------------------------------------
// Public API — locale management
// ---------------------------------------------------------------------------

/** Whether the widget ships translations for `code`. */
export function isSupportedLocale(code: string): code is Locale {
  return code === DEFAULT_LOCALE || Object.prototype.hasOwnProperty.call(LOCALE_LOADERS, code);
}

/**
 * Map a language code — a browser tag (`da-DK`) or a backend code (`no`) —
 * to the widget locale that serves it, or `null` if the widget has none.
 */
export function toWidgetLocale(code: string): Locale | null {
  const base = code.toLowerCase().split('-')[0] ?? '';
  const locale = BROWSER_LOCALE_ALIASES[base] ?? base;
  return isSupportedLocale(locale) ? locale : null;
}

/** The backend language code for a widget locale (`nb` → `no`). */
export function toApiLanguage(locale: Locale): string {
  return API_LANGUAGE[locale] ?? locale;
}

/**
 * Pick the widget locale from the browser's preferred languages: the first
 * entry whose base code is supported (`da-DK` → `da`), else `en`.
 *
 * React hosts can use it to get the same default as `init()`:
 * `await loadLocale(resolveBrowserLocale())`.
 */
export function resolveBrowserLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  const tags = navigator.languages.length > 0 ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const locale = toWidgetLocale(tag);
    if (locale) return locale;
  }
  return DEFAULT_LOCALE;
}

/** Return the currently active locale code. */
export function getLocale(): string {
  return activeLocale;
}

/**
 * Return the locale explicitly chosen via `setLocale()` or `loadLocale()`,
 * or `null` if none was. `getLocale()` reports `en` in both cases.
 */
export function getChosenLocale(): string | null {
  return chosenLocale;
}

/** Set the active locale. Does NOT load messages — call `loadLocale` first. */
export function setLocale(locale: string): void {
  activeLocale = locale;
  chosenLocale = locale;
}

/**
 * Register a locale's messages at runtime (e.g. for UMD consumers who bundle
 * translations themselves, or for custom / overridden translations).
 *
 * ```js
 * import sv from 'prezio-onboard-widget/locales/sv.json';
 * PrezioOnboard.registerLocale('sv', sv);
 * ```
 */
export function registerLocale(locale: string, messages: Record<string, unknown>): void {
  loaded[locale] = messages;
}

/**
 * Preload a locale so `t()` calls resolve synchronously.
 * Call this **before** rendering the widget when using a non-`en` locale.
 *
 * - Returns immediately for `en` or already-loaded locales.
 * - Sets the active locale on success.
 * - Falls back to `en` silently if the locale file is unavailable.
 */
export async function loadLocale(locale: string): Promise<void> {
  // Already available — just activate
  if (bundled[locale] || loaded[locale]) {
    setLocale(locale);
    return;
  }

  const loader =
    isSupportedLocale(locale) && locale !== DEFAULT_LOCALE ? LOCALE_LOADERS[locale] : undefined;
  if (!loader) {
    // Unknown locale — stay on current
    if (__DEV__) {
      console.warn(
        `[PrezioOnboard] Unknown locale "${locale}", falling back to "${DEFAULT_LOCALE}".`,
      );
    }
    return;
  }

  try {
    const mod = await loader();
    loaded[locale] = mod.default;
    setLocale(locale);
  } catch {
    // Load failed — stay on current locale; en fallback still works
    if (__DEV__) {
      console.warn(
        `[PrezioOnboard] Failed to load locale "${locale}", falling back to "${DEFAULT_LOCALE}".`,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Resolve helpers
// ---------------------------------------------------------------------------

function getMessages(locale: string): LocaleMessages {
  return bundled[locale] ?? loaded[locale] ?? bundled[DEFAULT_LOCALE] ?? {};
}

/** Walk a dot-separated path (`'steps.country.title'`) into nested objects. */
function resolve(messages: LocaleMessages, key: string): string | undefined {
  const parts = key.split('.');
  let current: unknown = messages;
  for (const part of parts) {
    if (typeof current !== 'object' || current === null) return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

// ---------------------------------------------------------------------------
// Public API — translation
// ---------------------------------------------------------------------------

/**
 * Translate a key using the active locale.
 *
 * - Nested keys: `t('steps.country.title')`
 * - Interpolation: `t('common.stepOf', { current: '3', total: '8' })`
 * - Falls back to the English bundle, then returns the raw key.
 */
export function t(key: string, params?: Record<string, string>): string {
  const locale = activeLocale;

  // Try active locale, then fall back to English
  let value = resolve(getMessages(locale), key);
  if (value === undefined && locale !== DEFAULT_LOCALE) {
    value = resolve(getMessages(DEFAULT_LOCALE), key);
  }
  if (value === undefined) return key;

  // Interpolate `{param}` placeholders
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      value = value.split(`{${k}}`).join(v);
    }
  }

  return value;
}
